import React, { useState, useEffect } from "react";
import API from "../api/axios";
import { useTranslation } from "react-i18next";

const S = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
.sv-page{min-height:100vh;background:var(--bg);font-family:var(--font-body);padding:40px 20px}
.sv-card{max-width:760px;margin:0 auto;background:var(--surface);border-radius:24px;padding:48px;border:1px solid var(--border);box-shadow:var(--shadow-lg);animation:svIn .5s ease}
@keyframes svIn{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
.sv-badge{display:inline-flex;align-items:center;gap:6px;padding:4px 14px;background:var(--accent-soft);border:1px solid var(--accent-soft);border-radius:20px;font-size:11px;font-weight:700;color:var(--accent);letter-spacing:1px;text-transform:uppercase;margin-bottom:20px}
.sv-title{font-family:var(--font-display);font-size:36px;color:var(--text);margin-bottom:12px;line-height:1.2}
.sv-author{font-size:13px;color:var(--text-3);margin-bottom:28px}
.sv-divider{height:1px;background:linear-gradient(90deg,var(--border),transparent);margin:24px 0}
.sv-section-label{font-size:11px;font-weight:700;color:var(--accent);text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px}
.sv-section-value{font-size:14px;color:var(--text);line-height:1.75;margin-bottom:22px}
.sv-tags{display:flex;flex-wrap:wrap;gap:7px;margin-bottom:22px}
.sv-tag{padding:5px 13px;background:var(--accent-tint);border:1px solid var(--accent-soft);border-radius:20px;font-size:12px;font-weight:600;color:var(--accent)}
.sv-action-row{display:flex;gap:12px;flex-wrap:wrap;margin-top:28px}
.sv-btn{padding:12px 24px;border-radius:12px;font-family:var(--font-body);font-size:13px;font-weight:700;cursor:pointer;transition:all .18s;text-decoration:none;display:inline-flex;align-items:center;gap:8px}
.sv-btn-primary{background:var(--accent);color:var(--on-accent);border:none}
.sv-btn-primary:hover{background:var(--accent-hover);transform:translateY(-1px)}
.sv-btn-ghost{background:transparent;color:var(--accent);border:1.5px solid var(--accent-soft)}
.sv-btn-ghost:hover{background:var(--accent-tint)}
.sv-file-chip{display:inline-flex;align-items:center;gap:6px;padding:8px 14px;background:var(--surface-2);border:1px solid var(--border);border-radius:10px;font-size:13px;color:var(--text);margin-bottom:6px}
.sv-watermark{text-align:center;margin-top:36px;font-size:12px;color:var(--text-3)}
.sv-watermark a{color:var(--accent);text-decoration:none}
.sv-watermark a:hover{color:var(--accent)}
.sv-not-found{text-align:center;padding:60px 0;color:var(--text-3);font-size:15px}
.sv-not-found-icon{font-size:48px;margin-bottom:16px;opacity:.3}
`;

const getFileType = (name = "") => {
  const e = "." + name.split(".").pop().toLowerCase();
  if ([".pdf", ".doc", ".docx", ".txt", ".md"].includes(e)) return "doc";
  if ([".js", ".ts", ".py", ".java", ".cpp", ".html", ".css", ".json"].includes(e)) return "code";
  if ([".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"].includes(e)) return "img";
  return "other";
};
const FICON = { doc: "📄", code: "💻", img: "🖼️", other: "📦" };

function ShareView() {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { t } = useTranslation();

  const shareId = window.location.pathname.split("/share/")[1];

  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = S;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await API.get(`/share/${shareId}`);
        setProject(res.data.project);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    if (shareId) fetchProject();
    else { setNotFound(true); setLoading(false); }
  }, [shareId]);

  if (loading) return (
    <div className="sv-page">
      <div className="sv-card">
        <div className="sv-not-found"><div className="sv-not-found-icon">⏳</div>{t("sv_loading")}</div>
      </div>
    </div>
  );

  if (notFound || !project) return (
    <div className="sv-page">
      <div className="sv-card">
        <div className="sv-not-found">
          <div className="sv-not-found-icon">🔒</div>
          {t("sv_notFound")}
          <div style={{ marginTop: 20 }}>
            <a href="/" className="sv-btn sv-btn-ghost" style={{ display: "inline-flex" }}>
              {t("sv_goToVS")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  const ft = project.file?.originalName ? getFileType(project.file.originalName) : null;

  return (
    <div className="sv-page">
      <div className="sv-card">
        <div className="sv-badge">{t("sv_shared")}</div>
        <h1 className="sv-title">{project.title}</h1>
        <p className="sv-author">
          {t("sv_sharedBy")} {project.createdBy?.name || "Unknown"} ·{" "}
          {new Date(project.createdAt).toLocaleDateString()}
        </p>
        <div className="sv-divider" />
        <div className="sv-section-label">{t("sv_description")}</div>
        <div className="sv-section-value">{project.description}</div>
        {project.techStack && (
          <>
            <div className="sv-section-label">{t("sv_techStack")}</div>
            <div className="sv-tags">
              {project.techStack.split(/[,·]+/).map((t2, i) => (
                <span key={i} className="sv-tag">{t2.trim()}</span>
              ))}
            </div>
          </>
        )}
        {(project.tags || []).length > 0 && (
          <>
            <div className="sv-section-label">{t("sv_tags")}</div>
            <div className="sv-tags">
              {project.tags.map(tag => <span key={tag} className="sv-tag">#{tag}</span>)}
            </div>
          </>
        )}
        {ft && (
          <>
            <div className="sv-section-label">{t("sv_attachedFile")}</div>
            <div className="sv-file-chip">{FICON[ft]} {project.file.originalName}</div>
            {ft === "img" && (
              <img src={project.file.url} alt="" style={{ marginTop: 12, maxWidth: "100%", borderRadius: 12, maxHeight: 300, objectFit: "contain" }} />
            )}
          </>
        )}
        <div className="sv-action-row">
          {project.githubLink && (
            <a href={project.githubLink} target="_blank" rel="noreferrer" className="sv-btn sv-btn-primary">
              {t("sv_viewGithub")}
            </a>
          )}
          {project.file?.url && ft !== "img" && (
            <a href={project.file.url} download={project.file.originalName} className="sv-btn sv-btn-ghost">
              {t("sv_downloadFile")}
            </a>
          )}
        </div>
        <div className="sv-watermark">
          {t("sv_watermark")} · <a href="https://vaultsphere.online">vaultsphere.online</a>
        </div>
      </div>
    </div>
  );
}

export default ShareView;